/**
 * Titillium Web `@font-face` generation for the export document — decision F2
 * (2026-08-25 doc).
 *
 * Why this is parameterised rather than a constant: the same export HTML is
 * rendered in two environments that can reach the font bytes in completely
 * different ways.
 *
 *  - **Browser** (the live preview): the faces are bundled assets, so the
 *    caller passes Vite-resolved URLs. Nothing is added to the JS bundle
 *    beyond the URLs themselves.
 *  - **Backend** (the real PDF): Playwright renders via `page.setContent()` on
 *    `about:blank`, which has no base URL, so *no* relative or absolute path
 *    can resolve — the only thing that works is embedding the bytes. The
 *    caller passes base64 `data:` URIs read from disk.
 *
 * That asymmetry is the entire reason the export was never actually rendered
 * in Titillium before: the shared stylesheet named the family but shipped no
 * reachable source, so Chromium fell back to a default sans. Since font
 * metrics drive text wrapping, and `computeTilePages` budgets row heights
 * against wrapped content, the preview and the export were also measuring
 * two different documents.
 */

/** One shipped face: its filename, and the CSS weight/style it provides. */
export interface TitilliumFontFile {
  file: string;
  weight: number;
  style: 'normal' | 'italic';
}

/**
 * The faces the export document actually uses: Regular for body text, SemiBold
 * for row labels, Bold for the palier numbers, Italic for the « Aucun contenu »
 * placeholder. Light (300) is deliberately absent — the export never asks for
 * it, and each embedded face costs ~85KB of base64 in every rendered document.
 */
export const TITILLIUM_FONT_FILES: readonly TitilliumFontFile[] = [
  { file: 'TitilliumWeb-Regular.ttf', weight: 400, style: 'normal' },
  { file: 'TitilliumWeb-Italic.ttf', weight: 400, style: 'italic' },
  { file: 'TitilliumWeb-SemiBold.ttf', weight: 600, style: 'normal' },
  { file: 'TitilliumWeb-Bold.ttf', weight: 700, style: 'normal' },
];

/** The family name the export stylesheet references. */
export const TITILLIUM_FAMILY = 'Titillium Web';

/**
 * Builds the `@font-face` block from caller-supplied sources, keyed by the
 * filenames in `TITILLIUM_FONT_FILES`. A face with no source is skipped rather
 * than emitted with a broken `url()`, so a partial map degrades to fewer
 * weights instead of a stylesheet Chromium has to error on.
 */
export function buildTitilliumFontFace(sources: Record<string, string>): string {
  return TITILLIUM_FONT_FILES.filter((face) => sources[face.file] !== undefined)
    .map((face) => {
      const source = sources[face.file];
      return [
        '@font-face {',
        `font-family: '${TITILLIUM_FAMILY}';`,
        `font-style: ${face.style};`,
        `font-weight: ${face.weight};`,
        'font-display: swap;',
        `src: url('${source}') format('truetype');`,
        '}',
      ].join(' ');
    })
    .join(' ');
}
